/** Dependency-free fuzzy matching for small client-side lists.
 *
 * Scoring favors exact substrings over scattered subsequences, and earlier
 * matches over later ones, so "veri" ranks "verifieddr.com" above
 * "cleverincs.com". Scores are only comparable for the same query.
 */

/** Score `query` against `text`. Higher is better; null means no match.
 * Matches when every query character appears in order in `text`. */
export function fuzzyScore(query: string, text: string): number | null {
	const q = query.toLowerCase();
	const t = text.toLowerCase();
	if (q.length === 0) return 0;
	if (q.length > t.length) return null;

	// Exact substring beats any subsequence; earlier and tighter is better.
	const index = t.indexOf(q);
	if (index !== -1) {
		return 1000 - index * 2 - (t.length - q.length);
	}

	// Subsequence walk: consecutive hits and word-boundary hits score extra.
	let score = 0;
	let ti = 0;
	let previousHit = -2;
	for (const char of q) {
		const hit = t.indexOf(char, ti);
		if (hit === -1) return null;
		score += 10;
		if (hit === previousHit + 1) score += 15;
		if (hit === 0 || t[hit - 1] === "." || t[hit - 1] === " ") score += 10;
		score -= Math.min(hit - ti, 20);
		previousHit = hit;
		ti = hit + 1;
	}
	return score - (t.length - q.length);
}

/** Best {@link fuzzyScore} across several candidate fields, skipping holes. */
export function fuzzyScoreFields(
	query: string,
	fields: Array<string | undefined | null>,
): number | null {
	let best: number | null = null;
	for (const field of fields) {
		if (!field) continue;
		const score = fuzzyScore(query, field);
		if (score !== null && (best === null || score > best)) best = score;
	}
	return best;
}
